import { useTranslation } from 'react-i18next';
import type { TakataCallFilters, TakataCallsFilterOptions } from './useTakataCalls';

interface TakataFilterSidebarProps {
  filters: TakataCallFilters;
  options?: TakataCallsFilterOptions;
  collapsed: boolean;
  onChange: (key: string, value: string) => void;
  onClear: () => void;
  onCollapsedChange: (collapsed: boolean) => void;
}

/** Free-text columns; the server matches these with a case-insensitive contains. */
const TEXT_FIELDS = [
  { key: 'physOut', label: 'TakataCalls.Col.PhysOut', fallback: 'PhysOut' },
  { key: 'actorCode', label: 'TakataCalls.Col.ActorCode', fallback: 'Actor Code' },
  { key: 'town', label: 'TakataCalls.Col.Town', fallback: 'Town' },
  { key: 'address', label: 'TakataCalls.Col.Address', fallback: 'Address' },
] as const;

/**
 * The filter rail to the left of the grid.
 *
 * Wave is deliberately absent: it is pinned by the campaign-wave selector in the
 * hero above. Everything here is a server-side filter — the keys match what
 * `TakataCallsController.ReadColumnFilter` reads out of the filter bag, so
 * renaming one here silently stops it filtering.
 */
export function TakataFilterSidebar({
  filters,
  options,
  collapsed,
  onChange,
  onClear,
  onCollapsedChange,
}: TakataFilterSidebarProps) {
  const { t } = useTranslation('mystery/dealerlist');

  const selects = [
    {
      key: 'brand',
      label: t('TakataCalls.Col.Brand', 'Brand'),
      values: options?.brands ?? [],
    },
    {
      key: 'quarter',
      label: t('TakataCalls.Col.Quarter', 'Quarter'),
      values: options?.quarters ?? [],
    },
    {
      key: 'status',
      label: t('TakataCalls.Col.Status', 'Status'),
      values: options?.statuses ?? [],
    },
    {
      key: 'outcomeDetails',
      label: t('TakataCalls.Col.OutcomeDetails', 'Outcome Details'),
      values: options?.outcomeDetails ?? [],
    },
  ];

  const dateRanges = [
    {
      label: t('TakataCalls.Col.CallDate', 'Call Date (CET)'),
      from: 'outcomeDateFrom',
      to: 'outcomeDateTo',
    },
    {
      label: t('TakataCalls.Col.AppointmentDate', 'Appointment date'),
      from: 'appointmentDateFrom',
      to: 'appointmentDateTo',
    },
  ];

  return (
    <aside className="tk-sidebar">
      <div className="tk-filters">
        <div className="tk-filters-head">
          <span className="tk-filters-title">{t('TakataCalls.Filters.Title', 'Filters')}</span>
          <div className="tk-filters-actions">
            <button type="button" className="tk-filters-clear" onClick={onClear}>
              {t('TakataCalls.Filters.Clear', 'Clear all')}
            </button>
            <button
              type="button"
              className="tk-filters-collapse"
              title={t('TakataCalls.Filters.Hide', 'Hide filters')}
              aria-label={t('TakataCalls.Filters.Hide', 'Hide filters')}
              onClick={() => onCollapsedChange(true)}
            >
              <span aria-hidden="true">«</span>
            </button>
          </div>
        </div>

        <div className="tk-filters-grid">
          {TEXT_FIELDS.map((field) => (
            <div className="tk-field" key={field.key}>
              <label htmlFor={`flt-${field.key}`}>{t(field.label, field.fallback)}</label>
              <input
                id={`flt-${field.key}`}
                type="text"
                className="tk-col-filter-input"
                autoComplete="off"
                placeholder={t('TakataCalls.Filter.Search', 'Search…')}
                value={filters[field.key] ?? ''}
                onChange={(event) => onChange(field.key, event.target.value)}
              />
            </div>
          ))}

          {selects.map((select) => (
            <div className="tk-field" key={select.key}>
              <label htmlFor={`flt-${select.key}`}>{select.label}</label>
              <select
                id={`flt-${select.key}`}
                className="tk-col-filter-select"
                value={filters[select.key] ?? ''}
                onChange={(event) => onChange(select.key, event.target.value)}
              >
                <option value="">{t('TakataCalls.Filter.All', 'All')}</option>
                {select.values.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          ))}

          {dateRanges.map((range) => (
            <div className="tk-field" key={range.from}>
              <label>{range.label}</label>
              {/* datetime-local, stacked rather than side by side: the two inputs
                  do not fit next to each other at the sidebar's width. */}
              <div className="tk-col-filter-daterange tk-col-filter-daterange--dt">
                <input
                  type="datetime-local"
                  className="tk-col-filter-date"
                  title={t('TakataCalls.Filter.From', 'From')}
                  value={filters[range.from] ?? ''}
                  onChange={(event) => onChange(range.from, event.target.value)}
                />
                <input
                  type="datetime-local"
                  className="tk-col-filter-date"
                  title={t('TakataCalls.Filter.To', 'To')}
                  value={filters[range.to] ?? ''}
                  onChange={(event) => onChange(range.to, event.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Only visible once the sidebar is collapsed: the rail that reopens it. */}
      <button
        type="button"
        className="tk-sidebar-expand"
        title={t('TakataCalls.Filters.Show', 'Show filters')}
        aria-label={t('TakataCalls.Filters.Show', 'Show filters')}
        onClick={() => onCollapsedChange(false)}
        aria-hidden={!collapsed}
      >
        <span aria-hidden="true">»</span>
        <span className="tk-expand-label">{t('TakataCalls.Filters.Title', 'Filters')}</span>
      </button>
    </aside>
  );
}
